import {isSelectiveAiDomains} from 'common/HelperFunctions'
import {useCallback, useEffect, useRef, useState} from 'react'
import {IntlShape} from 'react-intl'
import {useDispatch} from 'react-redux'

import {
  sendAdmissionApplicantChatMessage,
  sendAdmissionPublicChatMessage,
  sendChatMessage,
} from '../../../../../setup/redux/Actions/Chatbot/chatbot'
import {isAiHelpTicketReply, isSupportRelatedResponse} from '../assistant/assistantQueryRouter'
import {
  AssistantResponse,
  AssistantUnderstoodAs,
  CHAT_TURN_TIMEOUT_MESSAGE,
  extractAssistantResponse,
  isClarifyingPresentation,
  readAdmissionFormDisabledMessage,
  readAssistantSteps,
  readRateLimitMessage,
  readTurnTimeoutMessage,
  sanitizeClarificationForEcho,
} from '../assistant/assistantResponseContract'
import {AssistantSource, assistantSourceForCapability} from '../assistant/assistantTypes'
import type {AssistantChatEndpoint} from '../assistant/chatSurface'
import {resolveAttendanceContextForSend} from './attendanceQueryContextPolicy'
import {AttendanceV2Payload} from './attendanceV2Types'
import {
  AttendanceChatMessage,
  AttendanceChatSendExtras,
  ConversationHistoryItem,
} from './attendanceWizardTypes'
import {extractAttendanceContext} from './extractAttendanceContext'
import {extractChatbotAnswer} from './extractChatbotAnswer'
import {resolveAttendanceAnswerView} from './resolveAttendanceAnswerView'

type SendChatAction = (
  query: string,
  conversationHistory: ConversationHistoryItem[],
  menuSelectedPath: string | null,
  onSuccess: (response: unknown) => void,
  onFailure: (error: unknown) => void,
  chatbotMode: 'assistant',
  requestOptions?: AttendanceChatSendExtras | null
) => unknown

export type {AssistantChatEndpoint}

/** `admission_public` returns a promise and is not dispatched: it bypasses the saga on purpose. */
const SEND_ACTION_FOR_ENDPOINT: Record<AssistantChatEndpoint, SendChatAction> = {
  chatbot: sendChatMessage as SendChatAction,
  admission_applicant: sendAdmissionApplicantChatMessage as SendChatAction,
  admission_public: sendAdmissionPublicChatMessage as SendChatAction,
}

/**
 * How long a turn may go without a reply before the chat gives up on it.
 *
 * The request itself has no timeout (SagaHelper's fetch), and `handleSendMessage` refuses new
 * messages while one is in flight — so a reply that never came froze the chat until a proxy cut
 * the connection (60s on the AI server, 600s elsewhere), and that cut arrives as an unreadable
 * network error. The backend answers a readable 504 at 55s; this is the backstop above it.
 */
export const CHAT_TURN_WATCHDOG_MS = 75000

function getLatestAttendanceContextFromHistory(
  history: ConversationHistoryItem[]
): Record<string, unknown> | null {
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const ctx = history[i]?.attendanceContext
    if (ctx && typeof ctx === 'object') return ctx
  }
  return null
}

/**
 * Which board answered, read off the response. `capability` is the backend's own attribution
 * and wins outright. Without one (a clarification the backend has not bound yet, a pre-contract
 * answer) the turn is attendance only when it actually carried attendance data; otherwise it
 * is left unattributed so no support affordance is drawn under it.
 */
function resolveTurnSource(
  contract: AssistantResponse,
  payload: AttendanceV2Payload | null,
  attendanceContext: Record<string, unknown> | null
): AssistantSource | undefined {
  const attributed = assistantSourceForCapability(contract.capability)
  if (attributed) return attributed
  if (payload || attendanceContext) return 'attendance'
  return undefined
}

type UseAttendanceChatParams = {
  intl: IntlShape
  setMessages: React.Dispatch<React.SetStateAction<AttendanceChatMessage[]>>
  conversationHistory: ConversationHistoryItem[]
  setConversationHistory: React.Dispatch<React.SetStateAction<ConversationHistoryItem[]>>
  /** `admission_applicant` -> `admission/chat`; `admission_public` -> `admission/public-chat`
   *  (no token, no saga); everything else -> `chatbot/chat`. */
  endpoint?: AssistantChatEndpoint
}

/**
 * The assistant's send loop — one path for every message, whichever board ends up answering.
 *
 * The client does not route: the whole conversation history goes with every turn and the
 * backend decides between its boards from that evidence (it ignores any board named in the
 * request). What comes back states `presentation` and `capability`, and those — not a guess
 * made before the request left — decide what renders and which `source` the turn is stamped
 * with. The attendance-specific parts (context, disambiguation picks, v2 payloads) are extras
 * on top of that loop, not a separate one.
 */
export function useAttendanceChat({
  intl,
  setMessages,
  conversationHistory,
  setConversationHistory,
  endpoint = 'chatbot',
}: UseAttendanceChatParams) {
  const dispatch = useDispatch()
  const sendAction = SEND_ACTION_FOR_ENDPOINT[endpoint]
  const [isTyping, setIsTyping] = useState(false)

  const conversationHistoryRef = useRef(conversationHistory)
  const lastAttendanceContextRef = useRef<Record<string, unknown> | null>(
    getLatestAttendanceContextFromHistory(conversationHistory)
  )

  useEffect(() => {
    conversationHistoryRef.current = conversationHistory
    const latest = getLatestAttendanceContextFromHistory(conversationHistory)
    if (latest) {
      lastAttendanceContextRef.current = latest
    }
  }, [conversationHistory])

  const requestSessionRef = useRef(0)
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearWatchdog = useCallback(() => {
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current)
      watchdogRef.current = null
    }
  }, [])

  useEffect(() => clearWatchdog, [clearWatchdog])

  const t = useCallback((id: string) => intl.formatMessage({id}), [intl])

  const appendConversationHistoryTurn = useCallback(
    (turn: ConversationHistoryItem) => {
      const entry: ConversationHistoryItem = {
        query: turn.query,
        answer: turn.answer,
        ...(turn.attendanceContext ? {attendanceContext: turn.attendanceContext} : {}),
        ...(turn.source ? {source: turn.source} : {}),
        ...(turn.capability ? {capability: turn.capability} : {}),
        ...(turn.clarification ? {clarification: turn.clarification} : {}),
      }
      conversationHistoryRef.current = [...conversationHistoryRef.current, entry]
      setConversationHistory((prev) => [...prev, entry])
    },
    [setConversationHistory]
  )

  const appendUserMessage = useCallback(
    (id: string, text: string) => {
      setMessages((prev) => [
        ...prev,
        {
          id,
          text,
          sender: 'user',
          timestamp: new Date(),
        },
      ])
    },
    [setMessages]
  )

  /**
   * Attaches what the server understood a non-English question to be to the USER's own bubble.
   *
   * Never blocks the turn on confirming it — the answer is already on its way, and the people
   * who need this are the least able to audit a translation into a language they may not read.
   */
  const attachUnderstoodAs = useCallback(
    (messageId: string, understoodAs: AssistantUnderstoodAs) => {
      setMessages((prev) =>
        prev.map((message) => (message.id === messageId ? {...message, understoodAs} : message))
      )
    },
    [setMessages]
  )

  const appendBotMessage = useCallback(
    (
      text: string,
      extras?: Partial<Omit<AttendanceChatMessage, 'id' | 'sender' | 'timestamp'>>
    ) => {
      setMessages((prev) => [
        ...prev,
        {
          ...(extras ?? {}),
          id: `${Date.now()}-bot`,
          text,
          sender: 'bot',
          timestamp: new Date(),
        },
      ])
    },
    [setMessages]
  )

  const handleChatResponse = useCallback(
    (trimmed: string, userMessageId: string, response: unknown) => {
      const fallback = intl.formatMessage({id: 'CHATBOT.SORRY_ERROR'})
      const contract = extractAssistantResponse(response)
      // A `clarify` / `menu` turn is the question it asks: if the backend put it only in
      // `clarification.question` and left `answer` empty, that question is still the turn's
      // prose — without this the turn would fall through to the not-found message and its chips
      // would never render.
      const clarificationQuestion = isClarifyingPresentation(contract.presentation)
        ? contract.clarification?.question?.trim() || ''
        : ''
      const answer =
        contract.answer || clarificationQuestion || extractChatbotAnswer(response, fallback)
      const attendanceContext = extractAttendanceContext(response)
      // Sanitized once here: the same shape is echoed on the next turn and replayed in history.
      const clarificationForEcho = sanitizeClarificationForEcho(contract.clarification)
      // The ticket action follows the RESPONSE: any answer that talks about support or trouble
      // offers it, whatever the question was and whichever board answered. The help bot (the
      // selective AI domains) also offers it on every reply where it could not help, and
      // whenever its reply says to click "Create a support ticket" (`meta.offerTicket`).
      const offersTicket = (text: string) =>
        isSupportRelatedResponse(text) ||
        (isSelectiveAiDomains() && (contract.offerTicket || isAiHelpTicketReply(contract)))
      // The help bot's "Try one of these" chips follow only a turn it could not answer (a
      // decline or an unclear question), never a real answer.
      const suggestions =
        isSelectiveAiDomains() && contract.kind === 'answer' ? [] : contract.suggestions

      if (attendanceContext) {
        lastAttendanceContextRef.current = attendanceContext
      }

      if (contract.understoodAs) {
        attachUnderstoodAs(userMessageId, contract.understoodAs)
      }

      if (!answer?.trim() || answer === fallback) {
        const source = resolveTurnSource(contract, null, attendanceContext)
        const shownText = source === 'attendance' ? t('CHATBOT.ATTENDANCE.NOT_FOUND') : fallback
        appendBotMessage(shownText, {
          attendanceContext,
          source,
          offerTicket: offersTicket(shownText),
          // An empty attendance result can still carry widening offers; keep them clickable.
          presentation: contract.presentation,
          capability: contract.capability,
          // A clarify/menu turn that came back with no prose still has chips to offer.
          clarification: contract.clarification,
          ...(suggestions.length > 0 ? {suggestions} : {}),
        })
        appendConversationHistoryTurn({
          query: trimmed,
          answer: answer || fallback,
          attendanceContext,
          source,
          capability: contract.capability,
          clarification: clarificationForEcho,
        })
        return
      }

      const {displayText, payload} = resolveAttendanceAnswerView(contract, answer)
      const source = resolveTurnSource(contract, payload, attendanceContext)

      appendBotMessage(displayText, {
        v2Payload: payload,
        rawApiAnswer: answer,
        attendanceContext,
        source,
        offerTicket: offersTicket(answer),
        presentation: contract.presentation,
        capability: contract.capability,
        clarification: contract.clarification,
        ...(suggestions.length > 0 ? {suggestions} : {}),
        ...(contract.presentation === 'steps' ? {steps: readAssistantSteps(contract.data)} : {}),
      })
      appendConversationHistoryTurn({
        query: trimmed,
        answer,
        attendanceContext,
        source,
        capability: contract.capability,
        clarification: clarificationForEcho,
      })
    },
    [appendBotMessage, appendConversationHistoryTurn, attachUnderstoodAs, intl, t]
  )

  const initializeWelcome = useCallback(() => {
    setMessages([
      {
        id: 'welcome',
        text: t('CHATBOT.ATTENDANCE.WELCOME'),
        sender: 'bot',
        timestamp: new Date(),
      },
    ])
  }, [setMessages, t])

  const resetAssistiveState = useCallback(() => {
    /* no-op — v2 uses NL follow-ups only */
  }, [])

  const handleSendMessage = useCallback(
    (text: string, sendExtras?: AttendanceChatSendExtras) => {
      const trimmed = text?.trim()
      if (!trimmed || isTyping) return

      const isDisambiguationPick = Boolean(sendExtras?.disambiguationPick)

      const contextResolution = resolveAttendanceContextForSend({
        explicitContext: sendExtras?.attendanceContext,
        isDisambiguationPick,
      })

      // The bubble shows the friendly label (e.g. the student's name); the backend still receives the
      // precise query (e.g. `id 10796`) so it can resolve the selection without re-asking.
      const userMessageId = `${Date.now()}-user`
      appendUserMessage(userMessageId, sendExtras?.displayText?.trim() || trimmed)

      if (contextResolution.clearStoredContext) {
        lastAttendanceContextRef.current = null
      }

      const requestSession = ++requestSessionRef.current
      setIsTyping(true)

      // No reply in time: release the chat and ignore whatever arrives later for this turn.
      clearWatchdog()
      watchdogRef.current = setTimeout(() => {
        watchdogRef.current = null
        if (requestSession !== requestSessionRef.current) return
        requestSessionRef.current += 1
        setIsTyping(false)
        appendBotMessage(CHAT_TURN_TIMEOUT_MESSAGE)
      }, CHAT_TURN_WATCHDOG_MS)

      const attendanceContext = contextResolution.attendanceContext
      const pendingClarification = sanitizeClarificationForEcho(sendExtras?.pendingClarification)

      const requestOptions: AttendanceChatSendExtras | null =
        attendanceContext ||
        sendExtras?.disambiguationPick ||
        sendExtras?.capabilityId ||
        pendingClarification ||
        sendExtras?.pageContext
          ? {
              ...(attendanceContext ? {attendanceContext} : {}),
              ...(sendExtras?.disambiguationPick
                ? {disambiguationPick: sendExtras.disambiguationPick}
                : {}),
              ...(sendExtras?.capabilityId ? {capabilityId: sendExtras.capabilityId} : {}),
              ...(pendingClarification ? {pendingClarification} : {}),
              ...(sendExtras?.pageContext ? {pageContext: sendExtras.pageContext} : {}),
            }
          : null

      const request = sendAction(
        trimmed,
        // The WHOLE conversation, every board's turns: the backend binds follow-ups ("the
        // overall one", a reply to its own clarification) from this, and caps it itself.
        conversationHistoryRef.current,
        null,
        (response: unknown) => {
          if (requestSession !== requestSessionRef.current) return
          clearWatchdog()
          setIsTyping(false)
          handleChatResponse(trimmed, userMessageId, response)
        },
        (error: unknown) => {
          if (requestSession !== requestSessionRef.current) return
          clearWatchdog()
          setIsTyping(false)
          // Admission form switched off for this school: say so as the reply.
          const disabledText =
            endpoint === 'chatbot' ? null : readAdmissionFormDisabledMessage(error)
          if (disabledText) {
            appendBotMessage(disabledText)
            return
          }
          // Rate limited: the server's own "wait a moment" is the reply, not an apology.
          const rateLimitText = readRateLimitMessage(error)
          if (rateLimitText) {
            appendBotMessage(rateLimitText)
            return
          }
          // The backend gave up on the turn at its deadline: say so, rather than a bare apology.
          const timeoutText = readTurnTimeoutMessage(error)
          if (timeoutText) {
            appendBotMessage(timeoutText)
            return
          }
          // A transport failure says nothing about which board would have answered, so the
          // message is the generic one rather than the attendance-flavoured "not found".
          const errorText = t('CHATBOT.SORRY_ERROR')
          appendBotMessage(errorText, {offerTicket: isSupportRelatedResponse(errorText)})
        },
        'assistant',
        requestOptions
      )
      if (endpoint !== 'admission_public') dispatch(request as never)
    },
    [
      appendBotMessage,
      appendUserMessage,
      clearWatchdog,
      dispatch,
      endpoint,
      handleChatResponse,
      isTyping,
      sendAction,
      t,
    ]
  )

  return {
    isTyping,
    initializeWelcome,
    resetAssistiveState,
    handleSendMessage,
  }
}
